import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { TickCircleIcon } from '@nebula/icons/ui';
import nebulaTheme from '@nebula/theme';
import { useSelector } from 'react-redux';
import Analytics, { MilestoneInputEvent } from '@ecs-uk/web-common-analytics';
import {
  BackHomeButton,
  Container,
  IconContainer,
  StyledBodyMedium400,
  StyledTitleSmall500,
  TextContainer,
  BodyContent,
} from './index.styles';
import { getSubscriptionName, formatDateToReadable, CANCELLATION_TYPES } from '../../constants';
import daysLeftOfTrialInCurrentProductSubscription from '../../../../actions/ecd/resolvers/daysLeftOfTrialInCurrentProductSubscription';
import isPendingCancellation from '../../../../actions/ecd/resolvers/isPendingCancellation';
import eligibleForCeTrial from '../../../../actions/ecd/resolvers/eligibleForCeTrial';
import { getFreeTrialData } from '../../../../helpers/subscription';
import { products } from '../../../../constants';
import usePageView from '../../../../hooks/usePageView';

export default function ResultStage({
  onBackHome,
  subscriptionFamily,
  nextBillingDate,
  isDeferred,
  isImmediate,
  isFlexFlow,
}) {
  const subscriptionName = getSubscriptionName(subscriptionFamily);
  const formattedDate = formatDateToReadable(nextBillingDate);

  let bodyText = '';
  let cancellationType = 'not_set';

  const PAGE_NAME = 'ProdMoveCoolingOffResultPage';

  usePageView(PAGE_NAME);

  if (isDeferred) {
    bodyText = `Your benefits stay active until ${formattedDate}. We’ll stop future payments.`;
    cancellationType = CANCELLATION_TYPES.DEFERRED;
  } else if (isImmediate) {
    bodyText = 'You now have a free account. We’ll stop future payments and you’ll get a refund within 14 days.';
    cancellationType = CANCELLATION_TYPES.IMMEDIATE;
  }

  const reduxState = useSelector(state => state);
  const subscription = useSelector(state => state?.subscriptions?.current);
  const billing = useSelector(state => state?.billing);
  const [hasPublishedEvent, setHasPublishedEvent] = useState(false);

  useEffect(() => {
    if (isFlexFlow) {
      return;
    }

    if (!hasPublishedEvent && subscription && billing) {
      const daysLeftInTrial = daysLeftOfTrialInCurrentProductSubscription(reduxState);

      const pendingCancellation = isPendingCancellation(reduxState);

      const freeTrialData = getFreeTrialData(billing?.freeTrialTerms, subscription.freeTrialEndDt);
      const isInFreeTrial = freeTrialData.ends !== undefined;

      const isCeTrialEligible = eligibleForCeTrial(reduxState);

      const productMovementSuccessEvent = MilestoneInputEvent.fromObject({
        product_movement_success: {
          daysLeftOfTrialInCurrentProductSubscription: daysLeftInTrial,
          family: subscriptionFamily,
          isPendingCancellation: pendingCancellation,
          isWithinFreeTrial: isInFreeTrial,
          is_eligible_for_trial: isCeTrialEligible ?? 'not_set',
          payment_method: 'not_set',
          productSubscriptionLookingToMoveTo: products.basic,
          subscription_from: subscriptionFamily,
          subscription_to: products.basic,
          cancellationType,
        },
      });

      Analytics.publish(productMovementSuccessEvent);
      setHasPublishedEvent(true);
    }
  }, [hasPublishedEvent, subscription, billing, reduxState, subscriptionFamily, cancellationType, isFlexFlow]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Container>
      <BodyContent>
        <IconContainer aria-hidden="true">
          <TickCircleIcon iconSize="large" iconColor={nebulaTheme.color.ui.grey[100]} />
        </IconContainer>
        <TextContainer>
          <StyledTitleSmall500>{`${subscriptionName} subscription cancelled`}</StyledTitleSmall500>
          <StyledBodyMedium400>{bodyText}</StyledBodyMedium400>
        </TextContainer>
        <BackHomeButton label="Back to Home" onClick={onBackHome} />
      </BodyContent>
    </Container>
  );
}

ResultStage.propTypes = {
  onBackHome: PropTypes.func.isRequired,
  subscriptionFamily: PropTypes.string.isRequired,
  nextBillingDate: PropTypes.string,
  isDeferred: PropTypes.bool.isRequired,
  isImmediate: PropTypes.bool.isRequired,
  isFlexFlow: PropTypes.bool,
};

ResultStage.defaultProps = {
  nextBillingDate: null,
  isFlexFlow: false,
};
